import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/services/apiClient'
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline'

interface Trade {
  id: string
  user_id: string
  user_name: string
  user_email: string
  symbol: string
  order_type: string
  position_type: string
  quantity: number
  open_price: number
  close_price: number | null
  margin_required: number
  fee: number
  status: string
  pnl: number
  leverage: number
  open_time: string
  close_time: string | null
}

export default function TradesPage() {
  const [trades, setTrades] = useState<Trade[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [symbolFilter, setSymbolFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [total, setTotal] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    fetchTrades()
  }, [currentPage, searchTerm, statusFilter, symbolFilter])

  const fetchTrades = async () => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(symbolFilter !== 'all' && { symbol: symbolFilter }),
      })
      
      const response = await apiClient.get('/admin-trades', Object.fromEntries(params))
      if (response.success && response.data) {
        setTrades(response.data.trades || [])
        setTotal(response.data.pagination?.total || 0)
      }
    } catch (error) {
      console.error('Failed to fetch trades:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
    }).format(value)
  }

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { bg: string; text: string; label: string }> = {
      'open': { bg: 'bg-blue-100', text: 'text-blue-800', label: '持仓中' },
      'closed': { bg: 'bg-green-100', text: 'text-green-800', label: '已平仓' },
      'cancelled': { bg: 'bg-gray-100', text: 'text-gray-800', label: '已取消' },
      'liquidated': { bg: 'bg-red-100', text: 'text-red-800', label: '强制平仓' },
    }
    const config = statusMap[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status }
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    )
  }

  const getOrderTypeBadge = (type: string) => {
    const typeMap: Record<string, { bg: string; text: string; label: string }> = {
      'buy': { bg: 'bg-green-50', text: 'text-green-700', label: '买入' },
      'sell': { bg: 'bg-red-50', text: 'text-red-700', label: '卖出' },
    }
    const config = typeMap[type] || { bg: 'bg-gray-50', text: 'text-gray-700', label: type }
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    )
  }

  const getPositionTypeBadge = (type: string) => {
    const typeMap: Record<string, { bg: string; text: string; label: string }> = {
      'long': { bg: 'bg-blue-50', text: 'text-blue-700', label: '做多' },
      'short': { bg: 'bg-orange-50', text: 'text-orange-700', label: '做空' },
    }
    const config = typeMap[type] || { bg: 'bg-gray-50', text: 'text-gray-700', label: type }
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    )
  }

  const getPnlColor = (pnl: number) => {
    if (pnl > 0) return 'text-green-600'
    if (pnl < 0) return 'text-red-600'
    return 'text-gray-600'
  }

  return (
    <div className="page-content">
      <div className="mb-8">
        <h1 className="page-title">交易管理</h1>
        <p className="page-subtitle">管理用户交易订单</p>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="搜索用户邮箱或订单ID..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
                className="form-input pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setCurrentPage(1)
                }}
                className="form-input"
              >
                <option value="all">所有状态</option>
                <option value="open">持仓中</option>
                <option value="closed">已平仓</option>
                <option value="cancelled">已取消</option>
                <option value="liquidated">强制平仓</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={symbolFilter}
                onChange={(e) => {
                  setSymbolFilter(e.target.value)
                  setCurrentPage(1)
                }}
                className="form-input"
              >
                <option value="all">所有交易对</option>
                <option value="XAUUSD">XAUUSD (黄金)</option>
                <option value="EURUSD">EURUSD (欧美)</option>
                <option value="GBPUSD">GBPUSD (英美)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">订单ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">用户</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">交易对</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">类型</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">数量</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">开仓价</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">平仓价</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">杠杆</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">盈亏</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">开仓时间</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={11} className="px-6 py-4 text-center text-gray-500">
                    加载中...
                  </td>
                </tr>
              ) : !trades || trades.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-4 text-center text-gray-500">
                    暂无数据
                  </td>
                </tr>
              ) : (
                trades.map((trade) => (
                  <tr key={trade.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/trades/${trade.id}`)}>
                    <td className="px-6 py-4 text-sm text-gray-900 font-mono">{trade.id.substring(0, 8)}</td>
                    <td className="px-6 py-4 text-sm">
                      <div className="text-gray-900">{trade.user_name}</div>
                      <div className="text-xs text-gray-500">{trade.user_email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">{trade.symbol}</td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex gap-1">
                        {getOrderTypeBadge(trade.order_type)}
                        {getPositionTypeBadge(trade.position_type)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{trade.quantity}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{trade.open_price.toFixed(4)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {trade.close_price ? trade.close_price.toFixed(4) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{trade.leverage.toFixed(2)}x</td>
                    <td className={`px-6 py-4 text-sm font-semibold ${getPnlColor(trade.pnl)}`}>
                      {formatCurrency(trade.pnl)}
                    </td>
                    <td className="px-6 py-4 text-sm">{getStatusBadge(trade.status)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(trade.open_time).toLocaleDateString('zh-CN')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            共 {total} 条记录
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-secondary"
            >
              上一页
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage * 20 >= total}
              className="btn-secondary"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
